import { estimateFrameFt } from '../model/scale.ts';
import { TIME_PRESETS, type LightStyle, type Run, type TimePreset } from '../model/types.ts';
import type { SceneInput } from '../render/scene.ts';
import { buildPrompt, promptInputFor } from './prompts.ts';
import type { AiCanvas, PromptOptions } from './protocol.ts';

/**
 * Pure parts of the original app-side AI client (`ai/client.ts`): which runs count, the
 * wire options, guide/mask decisions, the "View prompt" preview and the AI scene input.
 * Rendering the photo / guide / mask images stays per platform.
 */

export interface PromptArgs {
  runs: Run[];
  style: LightStyle;
  colorId: string;
  customHex: string;
  time: TimePreset;
  windowGlow: boolean;
}

/** Runs with enough points to draw; anything else is ignored for AI guidance. */
export function drawableRuns(runs: Run[]): Run[] {
  return runs.filter((r) => r.points.length >= 2);
}

export function buildAiRequestOptions(args: Omit<PromptArgs, 'time'>): PromptOptions {
  const runs = drawableRuns(args.runs);
  return {
    colorId: args.colorId,
    customHex: args.customHex,
    layout: args.style.layout,
    spacingIn: args.style.spacingIn,
    pairGapIn: args.style.pairGapIn,
    runCount: runs.length,
    windowGlow: args.windowGlow,
  };
}

/** A guide is sent when at least one run can be drawn; a mask only for day renders with a guide. */
export function guidance(args: Pick<PromptArgs, 'runs' | 'time'>): { hasGuide: boolean; hasMask: boolean } {
  const hasGuide = drawableRuns(args.runs).length > 0;
  return { hasGuide, hasMask: hasGuide && args.time === 'day' };
}

/** The exact prompt the server will build for these settings (for "View prompt"). */
export function previewPrompt(args: PromptArgs): string {
  return buildPrompt(promptInputFor(buildAiRequestOptions(args), { ...guidance(args), time: args.time }));
}

/**
 * Scene input for the AI guide / mask, built at the canvas content size. `frameWidthFt` must be
 * `project.frameWidthFt ?? estimateFrameFt(runs, photo.width, photo.height)` (photo size, not
 * content size, so the feet value is resolution independent) — see `aiFrameWidthFt`.
 */
export function aiSceneInput(
  canvas: AiCanvas,
  args: Omit<PromptArgs, 'windowGlow'> & { frameWidthFt: number },
): SceneInput {
  return {
    width: canvas.content.width,
    height: canvas.content.height,
    time: TIME_PRESETS[args.time],
    runs: drawableRuns(args.runs),
    style: args.style,
    colorId: args.colorId,
    customHex: args.customHex,
    frameWidthFt: args.frameWidthFt,
  };
}

export function aiFrameWidthFt(manual: number | null, runs: Run[], photoW: number, photoH: number): number {
  return manual ?? estimateFrameFt(runs, photoW, photoH);
}
